import React, { useEffect, useMemo, useRef, useState } from 'react';
import './modernized_app.css';

const SUPPLY_STATUSES = [
  'Ordered',
  'In Transit',
  'Received On Base',
  'Picked Up',
  'In Work',
  'Installed',
  'Closed',
  'Cancelled',
];

const emptyForm = {
  id: '',
  tail: '',
  partNumber: '',
  nomenclature: '',
  quantity: 1,
  orderedAt: '',
  estimatedArrival: '',
  status: 'Ordered',
  criticalPath: false,
  requesterShop: '',
  requestedBy: '',
  requesterName: '',
  issueId: '',
  notes: '',
};

function toLocalInputValue(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function formatDateTime(value) {
  if (!value) return '--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '--';
  return `${date.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}`;
}

function isOpenSupplyStatus(status) {
  return !['Installed', 'Closed', 'Cancelled'].includes(status || '');
}

function getPersonnelDisplayName(person) {
  if (!person) return '';
  return [person.Rank, person['First Name'], person['Last Name']]
    .map(value => String(value || '').trim())
    .filter(Boolean)
    .join(' ');
}

export default function SupplyTab({ api, user, selectedBase, globalView }) {
  const [items, setItems] = useState([]);
  const [issues, setIssues] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [canManageSupply, setCanManageSupply] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showOpenOnly, setShowOpenOnly] = useState(true);
  const [notification, setNotification] = useState('');
  const notificationTimerRef = useRef(null);

  const notify = (message) => {
    setNotification(message);
    window.clearTimeout(notificationTimerRef.current);
    notificationTimerRef.current = window.setTimeout(() => setNotification(''), 3000);
  };

  const loadSupplyItems = async () => {
    try {
      const res = await api.get('/get-supply-items', {
        params: {
          email: user.email,
          location: selectedBase,
          global: user.is_superuser && globalView ? true : undefined,
        },
      });
      setItems(res.data.data || []);
      setCanManageSupply(Boolean(res.data.canManageSupply));
    } catch (err) {
      console.error('Failed to load supply items', err);
      notify('Unable to load supply tracker');
    }
  };

  const loadIssues = async () => {
    try {
      const res = await api.get('/get-issues-log', {
        params: {
          email: user.email,
          location: selectedBase,
        },
      });
      setIssues(res.data.data || []);
    } catch (err) {
      console.error('Failed to load issue options for supply tab', err);
    }
  };

  const loadPersonnel = async () => {
    try {
      const res = await api.get('/get-personnel', {
        params: {
          email: user.email,
          location: selectedBase,
        },
      });
      setPersonnel(res.data.data || []);
    } catch (err) {
      console.error('Failed to load personnel for supply requester lookup', err);
    }
  };

  useEffect(() => {
    loadSupplyItems();
    loadIssues();
    loadPersonnel();
    return () => window.clearTimeout(notificationTimerRef.current);
  }, [api, user.email, user.is_superuser, selectedBase, globalView]);

  const personnelByManNumber = useMemo(() => {
    const map = new Map();
    personnel.forEach(person => {
      const empNum = String(person['Emp Num'] || '').trim();
      if (empNum) map.set(empNum, person);
    });
    return map;
  }, [personnel]);

  const updateForm = (key, value) => {
    setForm(current => {
      const next = { ...current, [key]: value };
      if (key === 'requestedBy') {
        const requester = personnelByManNumber.get(String(value || '').trim());
        if (requester?.['Shop Name']) {
          next.requesterShop = requester['Shop Name'];
        }
        next.requesterName = getPersonnelDisplayName(requester);
      }
      return next;
    });
  };

  const startEdit = (item) => {
    setForm({
      id: item.id || '',
      tail: item.tail || '',
      partNumber: item.partNumber || '',
      nomenclature: item.nomenclature || '',
      quantity: item.quantity || 1,
      orderedAt: toLocalInputValue(item.orderedAt),
      estimatedArrival: toLocalInputValue(item.estimatedArrival),
      status: item.status || 'Ordered',
      criticalPath: Boolean(item.criticalPath),
      requesterShop: item.requesterShop || '',
      requestedBy: item.requestedBy || '',
      requesterName: item.requesterName || '',
      issueId: item.issueId || '',
      notes: item.notes || '',
    });
  };

  const clearForm = () => setForm(emptyForm);

  const saveSupplyItem = async () => {
    if (!canManageSupply) {
      notify('You have read-only access to Supply.');
      return;
    }
    if (!form.tail.trim() || !form.partNumber.trim()) {
      notify('Tail # and Part # are required.');
      return;
    }

    try {
      await api.post('/save-supply-item', {
        email: user.email,
        location: selectedBase,
        record: form,
      });
      clearForm();
      await loadSupplyItems();
      notify('Supply item saved');
    } catch (err) {
      console.error('Failed to save supply item', err);
      notify(err.response?.data?.message || 'Unable to save supply item');
    }
  };

  const deleteSupplyItem = async (item) => {
    if (!canManageSupply) {
      notify('You have read-only access to Supply.');
      return;
    }
    if (!window.confirm(`Delete supply item ${item.partNumber} for tail ${item.tail}?`)) return;

    try {
      await api.post('/delete-supply-item', {
        email: user.email,
        location: selectedBase,
        id: item.id,
      });
      if (form.id === item.id) clearForm();
      await loadSupplyItems();
      notify('Supply item deleted');
    } catch (err) {
      console.error('Failed to delete supply item', err);
      notify(err.response?.data?.message || 'Unable to delete supply item');
    }
  };

  const displayedItems = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    return items.filter(item => {
      const matchesOpen = !showOpenOnly || item.isOpen !== false;
      const matchesStatus = !statusFilter || item.status === statusFilter;
      const matchesSearch = !normalizedSearch || [
        item.tail,
        item.partNumber,
        item.nomenclature,
        item.requesterShop,
        item.requestedBy,
        item.requesterName,
        item.notes,
      ].some(value => String(value || '').toLowerCase().includes(normalizedSearch));
      return matchesOpen && matchesStatus && matchesSearch;
    });
  }, [items, search, statusFilter, showOpenOnly]);

  const openIssueOptions = useMemo(() => {
    const normalizedTail = form.tail.trim().toUpperCase();
    return issues
      .filter(issue => !issue.fixed)
      .filter(issue => !normalizedTail || String(issue.tail || '').trim().toUpperCase() === normalizedTail)
      .sort((left, right) => new Date(right.called || right.createdAt || 0) - new Date(left.called || left.createdAt || 0));
  }, [issues, form.tail]);

  const issueLabelById = useMemo(() => {
    const map = new Map();
    issues.forEach(issue => {
      map.set(issue.id, `${issue.tail || '--'} | ${issue.shopName || '--'} | ${formatDateTime(issue.called)} | ${issue.description || issue.redBall?.rb_discrepancy || 'No description'}`);
    });
    return map;
  }, [issues]);

  const summary = useMemo(() => {
    const openItems = items.filter(item => item.isOpen !== false);
    const criticalItems = openItems.filter(item => item.criticalPath);
    const overdueCritical = criticalItems.filter(item => {
      if (!item.estimatedArrival) return false;
      const eta = new Date(item.estimatedArrival);
      return !Number.isNaN(eta.getTime()) && eta.getTime() < Date.now();
    });
    const tailsImpacted = new Set(openItems.map(item => String(item.tail || '').trim().toUpperCase()).filter(Boolean)).size;
    return { open: openItems.length, critical: criticalItems.length, overdueCritical: overdueCritical.length, tailsImpacted };
  }, [items]);

  const criticalByTail = useMemo(() => {
    const map = new Map();
    items
      .filter(item => item.isOpen !== false && item.criticalPath && item.estimatedArrival)
      .forEach(item => {
        const tail = String(item.tail || '').trim().toUpperCase();
        if (!tail) return;
        const current = map.get(tail);
        if (!current || new Date(item.estimatedArrival) > new Date(current.estimatedArrival)) {
          map.set(tail, item);
        }
      });
    return Array.from(map.values()).sort((left, right) => new Date(left.estimatedArrival) - new Date(right.estimatedArrival));
  }, [items]);

  return (
    <div className="supply-tab">
      {notification ? <div className="status-tab__notice">{notification}</div> : null}
      <div className="supply-tab__header ops-page-header">
        <div className="ops-page-header__copy">
          <h2 className="ops-page-header__title">Supply</h2>
          <p className="status-tab__subtitle">
            Track parts by tail and identify when supply is the critical path to return an aircraft to FMC.
          </p>
        </div>
      </div>

      <div className="supply-summary-grid">
        <div className="status-kpi-card"><span>Open Parts</span><strong>{summary.open}</strong></div>
        <div className="status-kpi-card"><span>Critical Path</span><strong>{summary.critical}</strong></div>
        <div className="status-kpi-card"><span>Overdue Critical</span><strong>{summary.overdueCritical}</strong></div>
        <div className="status-kpi-card"><span>Tails Impacted</span><strong>{summary.tailsImpacted}</strong></div>
      </div>

      <section className="supply-entry-panel">
        <div className="reports-panel__header">
          <h3>{form.id ? 'Edit Supply Item' : 'Add Supply Item'}</h3>
          <span>Critical-path ETAs feed the Status tab when Status ETIC is blank.</span>
        </div>
        <div className="supply-form-grid">
          <label>Tail #<input value={form.tail} onChange={event => updateForm('tail', event.target.value.toUpperCase())} /></label>
          <label>Part #<input value={form.partNumber} onChange={event => updateForm('partNumber', event.target.value.toUpperCase())} /></label>
          <label>Description<input value={form.nomenclature} onChange={event => updateForm('nomenclature', event.target.value)} /></label>
          <label>Qty<input type="number" min="1" value={form.quantity} onChange={event => updateForm('quantity', event.target.value)} /></label>
          <label>Ordered At<input type="datetime-local" value={form.orderedAt} onChange={event => updateForm('orderedAt', event.target.value)} /></label>
          <label>Estimated Arrival<input type="datetime-local" value={form.estimatedArrival} onChange={event => updateForm('estimatedArrival', event.target.value)} /></label>
          <label>Status
            <select value={form.status} onChange={event => updateForm('status', event.target.value)}>
              {SUPPLY_STATUSES.map(status => <option key={status} value={status}>{status}</option>)}
            </select>
          </label>
          <label>Requested By Man #<input value={form.requestedBy} onChange={event => updateForm('requestedBy', event.target.value)} /></label>
          <label>Requester Name
            <input value={form.requesterName} onChange={event => updateForm('requesterName', event.target.value)} />
            <small>Auto-fills when the man # matches Personnel.</small>
          </label>
          <label>Requester Shop
            <input value={form.requesterShop} onChange={event => updateForm('requesterShop', event.target.value)} />
            <small>Auto-fills when the man # matches Personnel.</small>
          </label>
          <label className="supply-form-grid__linked-issue">Linked Issue
            <select value={form.issueId} onChange={event => updateForm('issueId', event.target.value)}>
              <option value="">No linked issue</option>
              {form.issueId && !openIssueOptions.some(issue => issue.id === form.issueId) ? (
                <option value={form.issueId}>{issueLabelById.get(form.issueId) || 'Previously linked issue'}</option>
              ) : null}
              {openIssueOptions.map(issue => (
                <option key={issue.id} value={issue.id}>
                  {issue.tail || '--'} | {issue.shopName || '--'} | {formatDateTime(issue.called)} | {issue.description || issue.redBall?.rb_discrepancy || 'No description'}
                </option>
              ))}
            </select>
            <small>Shows open issues for the selected tail.</small>
          </label>
          <label className="supply-form-grid__critical">
            <input
              type="checkbox"
              checked={form.criticalPath}
              onChange={event => updateForm('criticalPath', event.target.checked)}
            />
            Critical path to FMC
          </label>
          <label className="supply-form-grid__notes">Notes<textarea value={form.notes} onChange={event => updateForm('notes', event.target.value)} /></label>
        </div>
        <div className="supply-form-actions">
          <button type="button" onClick={saveSupplyItem} disabled={!canManageSupply}>
            {form.id ? 'Save Changes' : 'Add Supply Item'}
          </button>
          <button type="button" onClick={clearForm}>Clear Form</button>
        </div>
        {!canManageSupply ? <p className="supply-readonly-note">You can view Supply records, but your current role cannot edit them.</p> : null}
      </section>

      <div className="supply-content-grid">
        <section className="supply-table-panel">
          <div className="supply-toolbar">
            <input value={search} onChange={event => setSearch(event.target.value)} placeholder="Search tail, part, description, notes" />
            <select value={statusFilter} onChange={event => setStatusFilter(event.target.value)}>
              <option value="">All statuses</option>
              {SUPPLY_STATUSES.map(status => <option key={status} value={status}>{status}</option>)}
            </select>
            <label>
              <input type="checkbox" checked={showOpenOnly} onChange={event => setShowOpenOnly(event.target.checked)} />
              Open only
            </label>
          </div>
          <div className="table-shell">
            <table className="styled-table compact-table supply-table">
              <thead>
                <tr>
                  <th>Tail #</th>
                  <th>Part #</th>
                  <th>Description</th>
                  <th>Qty</th>
                  <th>Status</th>
                  <th>Ordered</th>
                  <th>ETA</th>
                  <th>Critical</th>
                  <th>Shop</th>
                  <th>Requester</th>
                  <th>Notes</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayedItems.map(item => (
                  <tr key={item.id} className={item.criticalPath && isOpenSupplyStatus(item.status) ? 'supply-table__critical-row' : ''}>
                    <td>{item.tail || '--'}</td>
                    <td>{item.partNumber || '--'}</td>
                    <td>{item.nomenclature || '--'}</td>
                    <td>{item.quantity || 1}</td>
                    <td><span className={`supply-status-pill supply-status-pill--${String(item.status || '').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>{item.status || '--'}</span></td>
                    <td>{formatDateTime(item.orderedAt)}</td>
                    <td>{formatDateTime(item.estimatedArrival)}</td>
                    <td>{item.criticalPath ? 'Yes' : 'No'}</td>
                    <td>{item.requesterShop || '--'}</td>
                    <td>{item.requesterName || item.requestedBy || '--'}</td>
                    <td>{item.notes || '--'}</td>
                    <td>
                      <button type="button" onClick={() => startEdit(item)}>Edit</button>
                      <button type="button" onClick={() => deleteSupplyItem(item)} disabled={!canManageSupply}>Delete</button>
                    </td>
                  </tr>
                ))}
                {displayedItems.length === 0 ? (
                  <tr><td colSpan={12}>No supply items match the current filters.</td></tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </section>

        <section className="reports-panel supply-critical-panel">
          <div className="reports-panel__header">
            <h3>Critical Supply ETAs</h3>
            <span>Latest critical-path ETA per tail</span>
          </div>
          <div className="reports-table-shell">
            <table className="reports-table">
              <thead>
                <tr>
                  <th>Tail</th>
                  <th>Part</th>
                  <th>ETA</th>
                </tr>
              </thead>
              <tbody>
                {criticalByTail.map(item => (
                  <tr key={`${item.tail}-${item.id}`}>
                    <td>{item.tail}</td>
                    <td>{item.partNumber}</td>
                    <td>{formatDateTime(item.estimatedArrival)}</td>
                  </tr>
                ))}
                {criticalByTail.length === 0 ? (
                  <tr><td colSpan={3}>No critical-path supply ETAs are currently open.</td></tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}
